import { box, button, column, row, select, text } from '@tiendanube/nube-sdk-ui';
import { CALENDAR_TEXT, WEEKDAY_NAMES, calendarWeeks, formatCursor, formatDate, monthName, openCursor, parseCursor, parseDate, shiftCursor, todayYmd, yearOptions, } from './calendar.js';
import { calendarIcon, chevronLeftIcon, chevronRightIcon } from './icons.js';
/** `01`–`12`, so the option value round-trips through `formatCursor` without padding logic. */
const pad2 = (n) => String(n).padStart(2, '0');
function headNode(f, o, cursor) {
    const styles = o.styles.datePicker;
    const { year, month } = parseCursor(cursor);
    const move = o.handlers?.onDateCursor;
    // The arrows are glyph-only, so `ariaLabel` is the only thing that names them. `button` takes
    // one in NubeSDK, and the legacy writes the same strings into `aria-label`.
    const nav = (key, months, label, glyph) => button({
        key,
        style: styles.nav,
        ariaLabel: label,
        children: glyph ? [glyph] : ['<'],
        ...(move ? { onClick: () => move(shiftCursor(cursor, months)) } : {}),
    });
    return row({
        key: 'head',
        style: styles.head,
        children: [
            nav('prev', -1, CALENDAR_TEXT.previousMonth, chevronLeftIcon()),
            select({
                key: 'month',
                name: `${f.key}__month`,
                // Passed for accessibility and hidden, like every other native label in the form.
                label: CALENDAR_TEXT.month,
                value: pad2(month),
                options: Array.from({ length: 12 }, (_, i) => ({
                    label: monthName(i + 1),
                    value: pad2(i + 1),
                })),
                style: styles.monthSelect,
                ...(move
                    ? {
                        onChange: (e) => move(formatCursor(year, Number(e.value ?? month))),
                    }
                    : {}),
            }),
            select({
                key: 'year',
                name: `${f.key}__year`,
                label: CALENDAR_TEXT.year,
                value: String(year),
                options: yearOptions(year).map((y) => ({ label: String(y), value: String(y) })),
                style: styles.yearSelect,
                ...(move
                    ? {
                        onChange: (e) => move(formatCursor(Number(e.value ?? year), month)),
                    }
                    : {}),
            }),
            nav('next', 1, CALENDAR_TEXT.nextMonth, chevronRightIcon()),
        ],
    });
}
function gridNode(f, o, cursor) {
    const styles = o.styles.datePicker;
    const { year, month } = parseCursor(cursor);
    const chosen = parseDate(o.state.values[f.key] ?? '');
    const today = todayYmd();
    const pick = o.handlers?.onDateSelect;
    const weekdays = row({
        key: 'dows',
        style: styles.weekdayRow,
        children: WEEKDAY_NAMES.map((name, i) => text({ key: `dow-${i}`, style: styles.weekday, children: [name] })),
    });
    const weeks = calendarWeeks(cursor).map((week, w) => row({
        key: `w-${w}`,
        style: styles.week,
        children: week.map((day, d) => {
            // A leading/trailing blank holds the column open. Never a button: an empty cell that
            // highlights on hover reads as a day the buyer failed to hit.
            if (day === null)
                return box({ key: `b-${w}-${d}`, style: styles.blank, children: [] });
            const selected = chosen !== null && chosen.year === year && chosen.month === month && chosen.day === day;
            const isToday = today.year === year && today.month === month && today.day === day;
            const iso = formatDate({ year, month, day });
            return button({
                // Defence in depth — the panel's cursor key already separates months (see the header).
                // The selected flag is here for the same reason `radioGroup`'s is: a cell whose only
                // change is its background must not keep the id it had when it was plain.
                key: `d-${day}${selected ? '-on' : ''}`,
                style: {
                    ...styles.day,
                    // Today's outline goes UNDER the selection, never beside it — one cell can be both.
                    ...(isToday && !selected ? styles.dayToday : {}),
                    ...(selected ? styles.daySelected : {}),
                },
                // A bare "17" tells a screen-reader user nothing about which month it is in.
                ariaLabel: iso,
                children: [String(day)],
                ...(pick ? { onClick: () => pick(f.key, iso) } : {}),
            });
        }),
    }));
    return [weekdays, ...weeks];
}
/**
 * "Limpiar" — shown only once the field HAS a value.
 *
 * Without it an optional date field is a one-way door: every cell in the grid sets a date and none
 * unsets one, so a buyer who opened the picker by accident could never hand back an empty field.
 */
function footNode(f, o) {
    const pick = o.handlers?.onDateSelect;
    if (!o.state.values[f.key])
        return [];
    return [
        row({
            key: 'foot',
            style: o.styles.datePicker.foot,
            children: [
                button({
                    key: 'clear',
                    style: o.styles.datePicker.clear,
                    children: [CALENDAR_TEXT.clear],
                    ...(pick ? { onClick: () => pick(f.key, '') } : {}),
                }),
            ],
        }),
    ];
}
/**
 * The whole control: the tappable field-shaped button, plus the calendar when it is open.
 *
 * Returned as an array so the caller can drop it between the label and the inline error without
 * knowing whether the picker is showing.
 */
export function dateControlNodes(f, o, opts) {
    const styles = o.styles.datePicker;
    const value = o.state.values[f.key] ?? '';
    const open = o.state.openDateKey === f.key;
    const toggle = o.handlers?.onDateToggle;
    // 🚨 No error variant, deliberately — see `fields.ts`'s `inputStyle`. The other controls stopped
    // restyling on error because doing so remounts them and destroys what the buyer typed; this one
    // is a button we draw, so it could keep a red border safely, but then one control in the form
    // would mark its error differently from every other. The legacy bundle borders nothing
    // (`setFieldError` writes the message and stops), so message-only is also the converged answer.
    const triggerStyle = opts.icon ? styles.triggerWithIcon : styles.trigger;
    const glyph = calendarIcon();
    const trigger = button({
        key: 'trigger',
        style: triggerStyle,
        // The field's own caption, so the control is named even when `hide_labels` is on. The value is
        // read from the button's text; this is what says WHICH field it belongs to.
        ariaLabel: f.label,
        children: [
            // 🚨 The value is drawn from state, so `drawsOwnValue('date')` is true and the caller MUST
            // repaint on every change. There is no host-owned input here keeping its own display.
            text({
                key: 'value',
                style: value ? styles.value : styles.placeholder,
                children: [value || opts.placeholder],
            }),
            ...(glyph ? [box({ key: 'glyph', style: styles.glyph, children: [glyph] })] : []),
        ],
        ...(toggle ? { onClick: () => toggle(f.key) } : {}),
    });
    // A plain `box`, not the `styled()` wrapper the text fields use: that wrapper exists to colour a
    // placeholder overlay and blank it on `:focus-within` (`inputWrapCss`), and this placeholder is a
    // real child of the control with its own colour. Blanking it on focus would empty the box the
    // moment the buyer tapped it.
    const wrapped = box({
        key: 'wrap',
        style: o.styles.inputWrap,
        children: [
            ...(opts.icon
                ? [box({ key: 'icon', style: o.styles.inputIcon, children: [opts.icon] })]
                : []),
            trigger,
        ],
    });
    if (!open)
        return [wrapped];
    // Where the grid is: the buyer's own month if they have chosen a date, else this one.
    const cursor = o.state.dateCursor ?? openCursor(value);
    return [
        wrapped,
        column({
            // 🚨 The cursor lives in the KEY. See this file's header — without it, navigating months
            // leaves every handler in the panel pointing at the month the buyer just left.
            key: `cal-${cursor}`,
            style: styles.panel,
            children: [headNode(f, o, cursor), ...gridNode(f, o, cursor), ...footNode(f, o)],
        }),
    ];
}
//# sourceMappingURL=datepicker.js.map